<template>
  <section>
    <!-- Page Header -->
    <div class="header">
      <div class="header-body">
        <div class="row align-items-end">
          <div class="col">
            <h1 class="header-title">Create New Ticket</h1>
          </div>
          <div class="col-auto">
            <router-link
              :to="{ name: 'Support.NewTickets.All' }"
              class="btn btn-outline-secondary"
            >
              <i class="fa fa-arrow-left mr-2"></i>
              Back to List
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Main Form -->
      <div class="col-12 col-lg-8">
        <b-card>
          <b-form @submit.prevent="submitForm">
            <b-row>
              <b-col cols="12">
                <b-form-group label="Subject" label-class="font-weight-bold">
                  <b-form-input
                    v-model="form.name"
                    placeholder="Brief description of your issue"
                    maxlength="100"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-group label="Type" label-class="font-weight-bold">
                  <b-form-select
                    v-model="form.type"
                    :options="typeList"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-group label="Priority" label-class="font-weight-bold">
                  <b-form-select
                    v-model="form.priority"
                    :options="priorityList"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-form-group label="Description" label-class="font-weight-bold">
                  <markdwn-editor
                    ref="toastuiEditor"
                    :options="editorOptions"
                    height="300px"
                    initialEditType="wysiwyg"
                    previewStyle="tab"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-form-group>
                  <b-button
                    v-if="!showAttachments"
                    variant="outline-secondary"
                    size="sm"
                    @click="showAttachments = true"
                  >
                    <i class="fa fa-paperclip mr-1"></i> Add Attachments
                  </b-button>
                  <div v-if="showAttachments" class="mt-2">
                    <UploadImages :max="5" @changed="handleImages" />
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <hr>

            <div class="d-flex justify-content-between align-items-center">
              <div>
              </div>
              <div>
                <router-link
                  :to="{ name: 'Support.NewTickets.All' }"
                  class="btn btn-outline-secondary mr-2"
                >
                  Cancel
                </router-link>
                <b-button
                  type="submit"
                  variant="success"
                  :disabled="submitting"
                >
                  <span v-if="submitting">
                    <b-spinner small class="mr-1"></b-spinner>
                    Creating...
                  </span>
                  <span v-else>
                    <i class="fa fa-check mr-1"></i>
                    Create Ticket
                  </span>
                </b-button>
              </div>
            </div>
          </b-form>
        </b-card>
      </div>

      <!-- Sidebar -->
      <div class="col-12 col-lg-4">
        <b-card header="Add Collaborators">
          <b-form-group label="Share with team members:" class="mb-2">
            <b-v-select
              v-model="selectedCollaborator"
              :searchable="true"
              :options="collaboratorOptions"
              placeholder="Select user..."
            />
          </b-form-group>
          <b-button
            block
            variant="outline-primary"
            size="sm"
            :disabled="!selectedCollaborator"
            @click="addCollaborator"
          >
            <i class="fa fa-plus mr-1"></i> Add
          </b-button>

          <div v-if="form.collaboratorUserIds.length > 0" class="mt-3">
            <small class="text-muted d-block mb-2">Selected Collaborators:</small>
            <div
              v-for="userId in form.collaboratorUserIds"
              :key="userId"
              class="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
            >
              <span class="small">{{ getCollaboratorName(userId) }}</span>
              <b-button
                variant="link"
                size="sm"
                class="text-danger p-0"
                @click="removeCollaborator(userId)"
              >
                <i class="fa fa-times"></i>
              </b-button>
            </div>
          </div>
        </b-card>

        <b-card header="Tips" class="mt-3">
          <ul class="small mb-0 pl-3">
            <li class="mb-2">Be specific in your subject line</li>
            <li class="mb-2">Include steps to reproduce if reporting a bug</li>
            <li class="mb-2">Attach screenshots if relevant</li>
            <li>Set priority based on business impact</li>
          </ul>
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import { Editor } from '@toast-ui/vue-editor';
import UploadImages from 'vue-upload-drop-images';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

var refOfHttp = null;
var refOfEditor = null;

var addImageBlobHook = function(blob, callback) {
  if (refOfEditor && refOfHttp) {
    var fileName = blob.name;
    var reader = new FileReader();
    reader.onload = function() {
      var formData = new FormData();
      formData.append('formFile', blob);
      formData.append('extension', fileName.split('.').pop());
      var headers = { 'Content-Type': 'multipart/form-data' };
      refOfHttp.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
        var oldContent = refOfEditor.invoke('getMarkdown');
        refOfEditor.invoke('setMarkdown', oldContent + '\r\n\r\n![' + fileName + '](' + resp.data.url + ')\r\n\r\n');
      });
    };
    reader.readAsDataURL(blob);
  }
  callback();
  return false;
};

export default {
  name: 'CreateTicket',
  mixins: [myCommonDataMixin],
  components: {
    'markdwn-editor': Editor,
    UploadImages
  },

  data: function() {
    return {
      form: {
        name: '',
        message: '',
        type: 'General Support',
        priority: 2,
        putIntoBackLog: false,
        collaboratorUserIds: []
      },
      submitting: false,
      showAttachments: false,
      uploadedFiles: [],
      selectedCollaborator: '',
      usersAll: [],

      typeList: [
        { value: 'General Support', text: 'General Support' },
        { value: 'Bug Report', text: 'Bug Report' },
        { value: 'New Feature Suggestion', text: 'New Feature Request' },
        { value: 'Feature Improvement', text: 'Feature Improvement' }
      ],
      priorityList: [
        { value: 1, text: 'Low' },
        { value: 2, text: 'Medium' },
        { value: 3, text: 'High' },
        { value: 4, text: 'Urgent' }
      ],

      editorOptions: {
        hooks: {
          addImageBlobHook: addImageBlobHook
        },
        minHeight: '200px',
        language: 'en-US',
        useCommandShortcut: true,
        hideModeSwitch: true,
        toolbarItems: [
          ['bold', 'italic', 'strike'],
          ['ul', 'ol'],
          ['link', 'image'],
          ['code', 'codeblock']
        ]
      }
    };
  },

  computed: {
    collaboratorOptions: function() {
      var self = this;
      var options = [];
      self.usersAll.forEach(function(usr) {
        if (!self.form.collaboratorUserIds.includes(usr.id)) {
          options.push({
            value: usr.id,
            text: usr.fullName + ' (' + usr.email + ')'
          });
        }
      });
      return options;
    }
  },

  mounted: function() {
    var self = this;
    this.loadUsers();
    refOfHttp = this.$http;

    this.$nextTick(function() {
      if (self.$refs.toastuiEditor) {
        refOfEditor = self.$refs.toastuiEditor;
      }
    });
  },

  methods: {
    loadUsers: function() {
      var self = this;
      self.$http
        .get('/api/userspublic/collab')
        .then(function(resp) {
          self.usersAll = resp.data;
        })
        .catch(function(err) {
          console.error('Failed to load users', err);
        });
    },

    handleImages: function(files) {
      var self = this;
      files.forEach(function(file) {
        if (!self.uploadedFiles.find(function(f) { return f.name === file.name; })) {
          var formData = new FormData();
          formData.append('formFile', file);
          var headers = { 'Content-Type': 'multipart/form-data' };
          self.$http.post('/api/common/tickets/imageUpload', formData, { headers }).then(function(resp) {
            self.uploadedFiles.push({
              name: file.name,
              size: file.size,
              url: resp.data.url
            });
          });
        }
      });
    },

    addCollaborator: function() {
      if (this.selectedCollaborator && !this.form.collaboratorUserIds.includes(this.selectedCollaborator)) {
        this.form.collaboratorUserIds.push(this.selectedCollaborator);
        this.selectedCollaborator = '';
      }
    },

    removeCollaborator: function(userId) {
      var index = this.form.collaboratorUserIds.indexOf(userId);
      if (index > -1) {
        this.form.collaboratorUserIds.splice(index, 1);
      }
    },

    getCollaboratorName: function(userId) {
      var user = this.usersAll.find(function(u) { return u.id === userId; });
      return user ? user.fullName : userId;
    },

    submitForm: function() {
      var self = this;

      if (!self.$refs.toastuiEditor) return;

      var message = self.$refs.toastuiEditor.invoke('getMarkdown');
      if (!message || !message.trim()) {
        self.$swal('Error', 'Please enter a description', 'warning');
        return;
      }

      if (!self.form.name || !self.form.name.trim()) {
        self.$swal('Error', 'Please enter a subject', 'warning');
        return;
      }

      // Append attachments to message
      if (self.uploadedFiles.length > 0) {
        message += '\n\n**Attachments:**';
        self.uploadedFiles.forEach(function(file) {
          message += '\n\n![' + file.name + '](' + file.url + ')';
        });
      }

      var userData = self.$store.getters['account/getUserData'];
      var payload = {
        name: self.form.name,
        message: message,
        type: self.form.type,
        priority: self.form.priority,
        putIntoBackLog: self.form.putIntoBackLog,
        collaboratorUserIds: self.form.collaboratorUserIds,
        userEmail: userData.email,
        userFullName: userData.fullName,
        userId: userData.id
      };

      self.submitting = true;

      self.$http
        .post('/api/common/tickets/', payload)
        .then(function(resp) {
          self.$swal('Success', 'Ticket created successfully', 'success');
          self.$router.push({
            name: 'Support.NewTickets.View',
            params: { id: resp.data.id }
          });
        })
        .catch(function(err) {
          console.error('Failed to create ticket', err);
          self.$swal('Error', 'Failed to create ticket', 'error');
        })
        .finally(function() {
          self.submitting = false;
        });
    }
  }
};
</script>
